import { SpotlightKey } from '@reach/interfaces/spotlights/spotlights.enum';

import {
	MessagePosition,
	OnboardingCondition,
	OnboardingConfig,
	SpotlightConfigItem,
} from '~app-client/onboarding/types';

const positionTop: MessagePosition = {
	overlayX: 'center',
	overlayY: 'bottom',
};

const positionRight: MessagePosition = {
	overlayX: 'start',
	overlayY: 'center',
};

const positionBottom: MessagePosition = {
	overlayX: 'center',
	overlayY: 'top',
};

const positionLeft: MessagePosition = {
	overlayX: 'end',
	overlayY: 'center',
};

const configJustPopUp = (
	notBefore: SpotlightConfigItem<SpotlightKey[]> = [],
	onlyWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = [],
	notWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = []
): OnboardingConfig => ({
	withPoint: false,
	withMessage: false,
	withMoreInfoButton: false,
	justPopUp: true,
	gotItButton: false,
	notBefore,
	onlyWhenConditions,
	notWhenConditions,
});

const configJustPoint = (
	notBefore: SpotlightConfigItem<SpotlightKey[]> = [],
	onlyWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = [],
	notWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = []
): OnboardingConfig => ({
	withPoint: true,
	withMessage: false,
	withMoreInfoButton: false,
	justPopUp: false,
	gotItButton: false,
	notBefore,
	onlyWhenConditions,
	notWhenConditions,
});

const configJustMessage = (
	gotItButton: boolean,
	moreInfo: boolean,
	messagePosition: SpotlightConfigItem<MessagePosition>,
	notBefore: SpotlightConfigItem<SpotlightKey[]> = [],
	onlyWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = [],
	notWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = []
): OnboardingConfig => ({
	withPoint: false,
	withMessage: true,
	withMoreInfoButton: moreInfo,
	justPopUp: false,
	gotItButton,
	notBefore,
	messagePosition,
	onlyWhenConditions,
	notWhenConditions,
});

const configPointWithMessage = (
	gotItButton: boolean,
	moreInfo: boolean,
	messagePosition: SpotlightConfigItem<MessagePosition>,
	notBefore: SpotlightConfigItem<SpotlightKey[]> = [],
	onlyWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = [],
	notWhenConditions: SpotlightConfigItem<OnboardingCondition[]> = []
): OnboardingConfig => ({
	withPoint: true,
	withMessage: true,
	withMoreInfoButton: moreInfo,
	justPopUp: false,
	gotItButton,
	notBefore,
	messagePosition,
	onlyWhenConditions,
	notWhenConditions,
});

export const ONBOARDING_CONFIG_MAP: Record<SpotlightKey, OnboardingConfig> = {
	// CORE
	[SpotlightKey.CORE__LOCATION]: configJustPopUp([], [], []),

	// HOME
	[SpotlightKey.HOME__GET_STARTED]: configJustPopUp([], [], {
		base: [],
		mobile: [OnboardingCondition.LATERAL_CHAT_OPENED],
	}),
	[SpotlightKey.HOME__NAVBAR_CREATION]: configPointWithMessage(
		true,
		false,
		{ base: positionTop, desktop: positionBottom },
		[SpotlightKey.HOME__GET_STARTED],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.HOME__NAVBAR_SEARCH]: configPointWithMessage(
		true,
		false,
		{ base: positionTop, desktop: positionRight },
		[SpotlightKey.HOME__NAVBAR_CREATION],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.HOME__NAVBAR_CHAT]: configPointWithMessage(
		true,
		false,
		{ base: positionTop, desktop: positionRight },
		[SpotlightKey.HOME__NAVBAR_SEARCH],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.HOME__NAVBAR_INTEGRATIONS_DESKTOP]: configPointWithMessage(
		// TODO: Remove
		true,
		false,
		positionRight,
		{ base: [], desktop: [SpotlightKey.HOME__NAVBAR_CHAT] },
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.HOME__NAVBAR_PROFILE]: configPointWithMessage(
		true,
		false,
		{ base: positionTop, desktop: positionRight },
		{
			base: [],
			mobile: [SpotlightKey.HOME__NAVBAR_CHAT],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),

	// CHAT
	[SpotlightKey.CHAT__INITIAL_INFO]: configJustPopUp([], [], []),
	[SpotlightKey.CHAT__SHOW_ALL_CHATS_MOBILE]: configPointWithMessage(
		false,
		false,
		positionBottom,
		[SpotlightKey.CHAT__INITIAL_INFO],
		[],
		[]
	),
	[SpotlightKey.CHAT__MESSAGE_OPEN_OPTIONS_MOBILE]: configJustPoint(
		[SpotlightKey.CHAT__SHOW_ALL_CHATS_MOBILE],
		[],
		[]
	),
	[SpotlightKey.CHAT__MESSAGE_SOURCE]: configPointWithMessage(
		false,
		false,
		positionBottom,
		{
			base: [],
			desktop: [SpotlightKey.CHAT__INITIAL_INFO],
			mobile: [SpotlightKey.CHAT__MESSAGE_OPEN_OPTIONS_MOBILE],
		},
		[],
		[]
	),
	[SpotlightKey.CHAT__OPTIONS_VIEW_SOURCES]: configPointWithMessage(
		false,
		false,
		positionBottom,
		[],
		{
			base: [],
			mobile: [OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		},
		[]
	),
	[SpotlightKey.CHAT__OPTIONS_CONVERT_TO_NOTE_DESKTOP]: configPointWithMessage(
		// TODO: Remove
		false,
		false,
		positionBottom,
		{ base: [], desktop: [SpotlightKey.CHAT__MESSAGE_SOURCE] },
		{
			base: [],
			mobile: [OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		},
		[]
	),

	// SEARCH
	[SpotlightKey.SEARCH__CHANGE_VIEW_MOBILE]: configPointWithMessage(
		true,
		false,
		positionBottom,
		[SpotlightKey.HOME__NAVBAR_PROFILE],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__CHANGE_VIEW_DESKTOP]: configPointWithMessage(
		true,
		false,
		positionBottom,
		[SpotlightKey.SEARCH__SELECT_MULTIPLE_WORKSPACES_DESKTOP],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__OPEN_NODE]: configPointWithMessage(
		true,
		false,
		positionBottom,
		{
			base: [],
			mobile: [SpotlightKey.SEARCH__CHANGE_VIEW_MOBILE],
			desktop: [SpotlightKey.SEARCH__CHANGE_VIEW_DESKTOP],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__CHANGE_WORKSPACE_MOBILE]: configJustPoint(
		{
			base: [SpotlightKey.SEARCH__OPEN_NODE],
			mobile: [SpotlightKey.SEARCH__CHANGE_VIEW_MOBILE],
			desktop: [SpotlightKey.SEARCH__CHANGE_VIEW_DESKTOP],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__SELECT_MULTIPLE_WORKSPACES_MOBILE]: configJustMessage(
		true,
		true,
		positionBottom,
		{
			base: [],
			mobile: [SpotlightKey.SEARCH__CHANGE_WORKSPACE_MOBILE],
		},
		[OnboardingCondition.SIDE_MENU_OPENED],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__SELECT_MULTIPLE_WORKSPACES_DESKTOP]: configJustMessage(
		true,
		true,
		positionRight,
		[SpotlightKey.HOME__GET_STARTED],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__SELECT_MULTIPLE_NODES_MOBILE]: configPointWithMessage(
		false,
		false,
		positionBottom,
		{ base: [], mobile: [SpotlightKey.SEARCH__SELECT_MULTIPLE_WORKSPACES_MOBILE] },
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__SELECT_MULTIPLE_NODES_DESKTOP]: configPointWithMessage(
		false,
		false,
		positionBottom,
		[SpotlightKey.SEARCH__CHANGE_VIEW_DESKTOP],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__SWIPE_CARDS]: configPointWithMessage(
		false,
		false,
		positionBottom,
		{
			base: [],
			desktop: [SpotlightKey.SEARCH__SELECT_MULTIPLE_NODES_DESKTOP],
			mobile: [SpotlightKey.SEARCH__SELECT_MULTIPLE_NODES_MOBILE],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__OPEN_LATERAL_CHAT]: configPointWithMessage(
		true,
		false,
		positionBottom,
		{
			base: [],
			desktop: [SpotlightKey.SEARCH__SELECT_MULTIPLE_NODES_DESKTOP],
			mobile: [SpotlightKey.SEARCH__SWIPE_CARDS],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),

	// SEARCH ADVANCED
	[SpotlightKey.SEARCH__ADVANCED__PROPERTIES__ADD]: configJustPoint(
		[],
		[OnboardingCondition.ADVANCED_SEARCH_VISIBLE],
		[]
	),
	[SpotlightKey.SEARCH__ADVANCED__PROPERTIES__CHANGE_OPERATOR]: configPointWithMessage(
		false,
		true,
		positionBottom,
		[SpotlightKey.SEARCH__ADVANCED__PROPERTIES__ADD],
		[OnboardingCondition.ADVANCED_SEARCH_VISIBLE],
		[]
	),
	[SpotlightKey.SEARCH__ADVANCED__NODES__ADD]: configJustPoint(
		[SpotlightKey.SEARCH__ADVANCED__PROPERTIES__ADD],
		[OnboardingCondition.ADVANCED_SEARCH_VISIBLE],
		[]
	),
	[SpotlightKey.SEARCH__ADVANCED__NODES__CHANGE_OPERATOR]: configJustPoint(
		[SpotlightKey.SEARCH__ADVANCED__PROPERTIES__ADD, SpotlightKey.SEARCH__ADVANCED__NODES__ADD],
		[OnboardingCondition.ADVANCED_SEARCH_VISIBLE],
		[]
	),
	[SpotlightKey.SEARCH__ADVANCED__EXCLUDE__ADD]: configPointWithMessage(
		false,
		false,
		positionBottom,
		[SpotlightKey.SEARCH__ADVANCED__PROPERTIES__ADD, SpotlightKey.SEARCH__ADVANCED__NODES__ADD],
		[OnboardingCondition.ADVANCED_SEARCH_VISIBLE],
		[]
	),

	// VISUALISER
	[SpotlightKey.SEARCH__VIEWS__VISUALISER__SHOW_LABELS]: configPointWithMessage(
		false,
		true,
		positionLeft,
		{
			base: [],
			mobile: [SpotlightKey.SEARCH__CHANGE_WORKSPACE_MOBILE],
			desktop: [SpotlightKey.SEARCH__CHANGE_VIEW_DESKTOP],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),

	// BOARD
	[SpotlightKey.SEARCH__VIEWS__BOARD__ZOOM]: configJustPoint(
		{
			base: [],
			mobile: [SpotlightKey.SEARCH__CHANGE_WORKSPACE_MOBILE],
			desktop: [SpotlightKey.SEARCH__CHANGE_VIEW_DESKTOP],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),

	// MAP
	[SpotlightKey.SEARCH__VIEWS__MAP__SEARCH_PLACES]: configPointWithMessage(
		false,
		false,
		positionLeft,
		{
			base: [],
			mobile: [SpotlightKey.SEARCH__CHANGE_WORKSPACE_MOBILE],
			desktop: [SpotlightKey.SEARCH__CHANGE_VIEW_DESKTOP],
		},
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__VIEWS__MAP__FILTER_LOCATIONS]: configPointWithMessage(
		false,
		false,
		positionLeft,
		[SpotlightKey.SEARCH__VIEWS__MAP__SEARCH_PLACES],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__VIEWS__MAP__LOCATION_PROPERTIES]: configJustMessage(
		false,
		false,
		positionTop,
		[],
		[OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__VIEWS__MAP__OPTIONS_DETAILS]: configJustMessage(
		false,
		false,
		positionRight,
		[],
		[OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__VIEWS__MAP__CARD_RELATIONSHIPS]: configJustMessage(
		false,
		false,
		positionRight,
		[],
		[OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.SEARCH__VIEWS__MAP__CARD_PROPERTIES]: configJustMessage(
		false,
		false,
		positionRight,
		[],
		[OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),

	// NODE INFO
	[SpotlightKey.NODE_INFO__INITIAL]: configJustPopUp([], [], {
		base: [],
		mobile: [OnboardingCondition.LATERAL_CHAT_OPENED],
	}),
	[SpotlightKey.NODE_INFO__OPEN_CHAT]: configPointWithMessage(
		false,
		false,
		positionBottom,
		[SpotlightKey.NODE_INFO__INITIAL],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.NODE_INFO__RELATIONSHIPS__ADD]: configPointWithMessage(
		false,
		true,
		positionBottom,
		[SpotlightKey.NODE_INFO__OPEN_CHAT],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.NODE_INFO__RELATIONSHIPS__RECOMMENDATIONS]: configJustMessage(
		false,
		true,
		positionTop,
		[],
		[OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.NODE_INFO__RELATIONSHIPS__EXTRACTED]: configJustMessage(
		false,
		false,
		positionTop,
		[SpotlightKey.NODE_INFO__RELATIONSHIPS__RECOMMENDATIONS],
		[OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.NODE_INFO__RELATIONSHIPS__CARDS__SWIPE]: configPointWithMessage(
		false,
		false,
		positionBottom,
		[SpotlightKey.NODE_INFO__RELATIONSHIPS__ADD],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.NODE_INFO__RELATIONSHIPS__CARDS__OTHER_WORKSPACES]: configJustMessage(
		false,
		false,
		positionBottom,
		[
			SpotlightKey.NODE_INFO__RELATIONSHIPS__ADD,
			SpotlightKey.NODE_INFO__RELATIONSHIPS__CARDS__SWIPE,
		],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.NODE_INFO__PROPERTIES__ADD]: configPointWithMessage(
		false,
		true,
		positionBottom,
		[SpotlightKey.NODE_INFO__INITIAL],
		[],
		{ base: [], mobile: [OnboardingCondition.LATERAL_CHAT_OPENED] }
	),
	[SpotlightKey.NODE_INFO__WORKSPACES]: configJustMessage(false, false, positionTop, [], [], {
		base: [],
		mobile: [OnboardingCondition.LATERAL_CHAT_OPENED],
	}),
	[SpotlightKey.NODE_INFO__VISIT_WEBSITE]: configJustPoint([], [], {
		base: [],
		mobile: [OnboardingCondition.LATERAL_CHAT_OPENED],
	}),

	// NODE CREATION
	[SpotlightKey.NODE_CREATION__INTEGRATIONS]: configJustMessage(
		false,
		false,
		positionTop,
		[],
		[OnboardingCondition.GENERIC_MENU_FULLY_OPENED],
		[]
	),
	[SpotlightKey.NODE_CREATION__NODE_TYPE__NOTE]: configJustPoint([], [], []),
	[SpotlightKey.NODE_CREATION__NODE_TYPE__TAG]: configJustPoint([], [], []),
	[SpotlightKey.NODE_CREATION__NODE_TYPE__FILE]: configJustPoint([], [], []),
	[SpotlightKey.NODE_CREATION__NODE_TYPE__WEBSITE]: configJustPoint([], [], []),
	[SpotlightKey.NODE_CREATION__NODE_TYPE__WORKSPACE]: configJustPoint([], [], []),

	// PROFILE
	[SpotlightKey.PROFILE__TUTORIALS]: configJustPoint([], [], []),
	[SpotlightKey.PROFILE__RANKS]: configJustPoint([SpotlightKey.PROFILE__TUTORIALS], [], []),
	[SpotlightKey.PROFILE__RANKS_MODAL]: configJustPopUp([], [], []),
	[SpotlightKey.PROFILE__ACHIEVEMENTS]: configJustPopUp([], [], []),
	[SpotlightKey.PROFILE__ACHIEVEMENTS__ROTATE_CARD]: configJustPoint([], [], []),
	[SpotlightKey.PROFILE__INTEGRATIONS__ADD]: configPointWithMessage(
		false,
		false,
		positionBottom,
		[],
		[],
		[]
	),

	// INTEGRATIONS
	[SpotlightKey.INTEGRATIONS__FIRST_TIME]: configJustPopUp([], [], []),

	// MISC
	[SpotlightKey.CHAT_AI_LEARN_ALL]: configJustPopUp([], [], []),
};
